/**
 * UnifiedPostDialog — single detail / edit dialog for every post-like entity.
 *
 * Refactored from `PostExampleEditorDialog`. Consumes `PostLike` so the same
 * dialog can render:
 *   - Post examples (projects/{pid}/post_examples/*)
 *   - Draft posts under post_generations/{runId}/generatedPosts/*
 *   - Canonical content_posts/*
 *   - Sprint posts under content_strategies/{sid}/sprints/{sprintId}/posts/*
 *
 * All writes go through caller-provided callbacks (`onSave`, `onDelete`,
 * `onGenerateImage`, `onUploadReference`, `onAssetPersist`, `onPromoteToExample`).
 * This keeps the dialog agnostic of storage layout — each surface wires the
 * right Firestore hook / API client call.
 *
 * The smart Firestore merge logic (preserves local prompt typing while folding
 * generation-result fields from the live subscription) is ported verbatim
 * from PostExampleEditorDialog because it solves a real focus-theft bug
 * (see MODULE.md entry A5).
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Stack, Box, TextField, Alert, Chip, Typography,
  Accordion, AccordionSummary, AccordionDetails,
  Tabs, Tab, Button, Dialog, DialogActions, DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { BaseDialog, DialogHeader, DialogContent, DialogFooter } from '@modules/ui-components';
import type { FooterActionConfig } from '@modules/ui-components/dialogs/DialogFooter';
import {
  FacebookPagePublishPanel,
  type FacebookPagesPublicationStatus,
} from '@modules/facebook-pages-connect';
import type { PlatformId, AiAssetSlot } from '../../types';
import type { SocialDelivery } from '@modules/shared-types';
import { XManualPublicationPanel } from '../engagement/XManualPublicationPanel';
import type { PostLike } from '../../utils/postLike';
import { AiAssetsEditor, type GenerateImageConfig, type GenerateImageResult } from '../postExamples/AiAssetsEditor';
import { DataUsageList } from '../DataUsageList';
import { useSystemPlatforms } from '@modules/content-plan/hooks/useSystemPlatforms';
import { DesignBriefDisplay } from '../designBrief';
import type { DesignAssetBrief } from '@segmently/schema-models/content-plan';
import {
  emitDesignBriefRegenerated,
  emitPostRegeneratedFromBrief,
} from '../../telemetry/designBriefEvents';

// ─── Public API ──────────────────────────────────────────────────

export interface UnifiedPostDialogProps {
  open: boolean;
  onClose: () => void;
  /** The entity being viewed / edited. Null shows an empty dialog (create flow). */
  post: PostLike | null;
  /** Live subscription state — used to fold generation-result fields without focus theft. */
  live?: PostLike | null;
  /** When true, all inputs are disabled and Save / Delete buttons hidden. */
  readOnly?: boolean;
  /** Additional read-only footer actions, e.g. "Open editable location". */
  readOnlyAdditionalActions?: FooterActionConfig[];
  /**
   * Persist the current draft. Must resolve with the id (so image gen can
   * dispatch against the now-existing Firestore doc on first Save).
   */
  onSave: (next: PostLike) => Promise<string>;
  /** Optional delete — shows a Delete button in the header when provided. */
  onDelete?: () => Promise<void>;
  /** Kicks off image generation. Same fire-and-forget contract as PostExampleEditor. */
  onGenerateImage?: (cfg: GenerateImageConfig) => Promise<GenerateImageResult>;
  /** Uploads a reference image and returns `{url, mimeType}`. */
  onUploadReference?: (file: File) => Promise<{ url: string; mimeType: string }>;
  /** Called on discrete asset mutations (upload, URL paste) to flush draft to Firestore. */
  onAssetPersist?: (next: Record<string, AiAssetSlot>) => Promise<void> | void;
  /**
   * "Save as example" — promote a post into the PostExample library. Shown only
   * for non-example ownerKinds (`content_post_draft` / `content_post` / `sprint_post`).
   */
  onPromoteToExample?: () => Promise<void>;

  /**
   * H.4 — Multi-platform tabs. When this map is provided AND has > 1 entry,
   * the dialog renders a Tabs strip at the top; each tab loads that
   * platform's derivative into the form state. When the user switches tab,
   * pending edits flush via onSelectPlatform (caller can handle save-on-blur).
   *
   * Caller's typical map: each key is a PlatformId, value is a PostLike
   * derived from `posts.where(p => p.sourcePostId === masterId)`.
   */
  derivativesByPlatform?: Partial<Record<PlatformId, PostLike>>;

  /** H.4 — current selected platform tab (controlled). When omitted, the
   *  dialog manages it internally starting from the first key. */
  selectedPlatform?: PlatformId;

  /** H.4 — fired when user changes tab. Caller swaps `post` to the new
   *  derivative (form state re-syncs via the existing useEffect on `post`). */
  onSelectPlatform?: (platform: PlatformId) => void;

  /**
   * H.4 — "Refine with prompt..." — when provided, a button surfaces in
   * the header next to Promote that opens a textarea modal. On confirm the
   * caller dispatches a regenerate-content-post task with the userPrompt.
   */
  onRefine?: (userPrompt: string) => Promise<void>;

  /**
   * v1.8.0 — Re-generate the DesignAssetBrief WITHOUT touching writer
   * text. Caller dispatches `POST /regenerate-brief` and refreshes the
   * post doc. When omitted the brief section still renders but
   * regenerate buttons are hidden.
   */
  onRegenerateBrief?: () => Promise<void>;
  /**
   * v1.8.0 — Re-run the full chain (writer + design pipeline + assets).
   * Caller dispatches `POST /regenerate-post`. Master regen marks
   * dependent derivatives as `staleSinceMasterRegen` server-side.
   */
  onRegeneratePost?: () => Promise<void>;

  /** ContentPublication context used by the Facebook Pages publishing panel. */
  publicationId?: string;
  publicationStrategyId?: string;
  publicationPlatform?: string;
  publicationScheduledAt?: string;
  publicationFacebookStatus?: FacebookPagesPublicationStatus | null;
  publicationDelivery?: SocialDelivery;
}

const TESTIDS = {
  dialog: 'post-like-dialog',
  title: 'post-like-dialog-title',
  platforms: 'post-like-dialog-platforms',
  hook: 'post-like-dialog-hook',
  body: 'post-like-dialog-body',
  cta: 'post-like-dialog-cta',
  notes: 'post-like-dialog-notes',
  sourceUrl: 'post-like-dialog-source-url',
  sourceHandle: 'post-like-dialog-source-handle',
  save: 'post-like-dialog-save',
  cancel: 'post-like-dialog-cancel',
  delete: 'post-like-dialog-delete',
  promote: 'post-like-dialog-promote-to-example',
  audit: 'post-like-dialog-audit-accordion',
} as const;

// ─── Component ──────────────────────────────────────────────────

export const UnifiedPostDialog: React.FC<UnifiedPostDialogProps> = ({
  open, onClose, post, live, readOnly,
  readOnlyAdditionalActions,
  onSave, onDelete, onGenerateImage, onUploadReference, onAssetPersist,
  onPromoteToExample,
  derivativesByPlatform, selectedPlatform, onSelectPlatform, onRefine,
  onRegenerateBrief, onRegeneratePost,
  publicationId, publicationStrategyId, publicationPlatform, publicationScheduledAt, publicationFacebookStatus, publicationDelivery,
}) => {
  const [briefBusy, setBriefBusy] = useState(false);
  const designAssetBrief = ((live?.designAssetBrief ?? post?.designAssetBrief) as
    | DesignAssetBrief
    | undefined);
  const isMasterPost = (post?.generationMode ?? '') === 'master';
  const handleRegenerateBrief = onRegenerateBrief
    ? async () => {
        setBriefBusy(true);
        try {
          await onRegenerateBrief();
          emitDesignBriefRegenerated({
            postId: post?.id ?? '',
            projectId: post?.projectId ?? '',
            platform: post?.platforms?.[0],
            designFunction: designAssetBrief?.designFunction?.primary,
            briefStatus: designAssetBrief?.status,
          });
        } finally {
          setBriefBusy(false);
        }
      }
    : undefined;
  const handleRegeneratePost = onRegeneratePost
    ? async () => {
        setBriefBusy(true);
        try {
          await onRegeneratePost();
          emitPostRegeneratedFromBrief({
            postId: post?.id ?? '',
            projectId: post?.projectId ?? '',
            platform: post?.platforms?.[0],
            postKind: (post?.generationMode ?? 'direct') as
              | 'master'
              | 'derivative'
              | 'direct',
            staleDerivativesCount: 0,
            briefStatus: designAssetBrief?.status,
          });
        } finally {
          setBriefBusy(false);
        }
      }
    : undefined;
  const { platforms: systemPlatforms } = useSystemPlatforms({ activeOnly: true });
  const platformKeys = derivativesByPlatform
    ? (Object.keys(derivativesByPlatform) as PlatformId[])
    : [];
  const showPlatformTabs = platformKeys.length > 1;
  const [internalSelectedPlatform, setInternalSelectedPlatform] = useState<PlatformId | undefined>(undefined);
  const effectivePlatform = selectedPlatform ?? internalSelectedPlatform ?? platformKeys[0];

  // H.4 — Refine prompt modal state
  const [refineOpen, setRefineOpen] = useState(false);
  const [refinePrompt, setRefinePrompt] = useState('');
  const [refining, setRefining] = useState(false);
  const isExample = post?.ownerKind === 'author_post_example';
  const isCreate = !post || !post.id;

  const [persistedId, setPersistedId] = useState<string>('');
  const [title, setTitle] = useState('');
  const [hook, setHook] = useState('');
  const [body, setBody] = useState('');
  const [cta, setCta] = useState('');
  const [notes, setNotes] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [sourceCreatorHandle, setSourceCreatorHandle] = useState('');
  const [platforms, setPlatforms] = useState<PlatformId[]>([]);
  const [aiAssets, setAiAssets] = useState<Record<string, AiAssetSlot>>({});
  const [submitting, setSubmitting] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectablePlatforms = useMemo(() => {
    const ids = new Set<string>();
    systemPlatforms.forEach((platform) => ids.add(platform.platformId));
    platformKeys.forEach((platform) => ids.add(platform));
    (post?.platforms ?? []).forEach((platform) => ids.add(String(platform)));
    platforms.forEach((platform) => ids.add(String(platform)));
    return Array.from(ids).sort() as PlatformId[];
  }, [systemPlatforms, platformKeys, post?.platforms, platforms]);

  useEffect(() => {
    if (!open) return;
    setPersistedId(post?.id ?? '');
    setTitle(post?.title ?? '');
    setHook(post?.hook ?? '');
    setBody(post?.body ?? '');
    setCta(post?.cta ?? '');
    setNotes(post?.notes ?? '');
    setSourceUrl(post?.sourceUrl ?? '');
    setSourceCreatorHandle(post?.sourceCreatorHandle ?? '');
    setPlatforms((post?.platforms ?? []) as PlatformId[]);
    setAiAssets((post?.aiAssets ?? {}) as Record<string, AiAssetSlot>);
    setError(null);
    setSubmitting(false);
    setPromoting(false);
  }, [open, post]);

  // Smart Firestore-merge — fold ONLY generation-result fields from live into
  // local state. Preserves user-typed prompt / style / aspect edits.
  // (Ported from PostExampleEditorDialog — see MODULE.md bug A5.)
  useEffect(() => {
    if (!open || !live || live.id !== persistedId) return;
    const liveAssets = (live.aiAssets ?? {}) as Record<string, AiAssetSlot>;
    setAiAssets((prev) => {
      let changed = false;
      const next: Record<string, AiAssetSlot> = { ...prev };
      for (const [slotKey, liveSlot] of Object.entries(liveAssets)) {
        const prevSlot = prev[slotKey];
        if (!prevSlot) {
          next[slotKey] = liveSlot;
          changed = true;
          continue;
        }
        const liveExps = (liveSlot.experiments ?? {}) as Record<string, any>;
        const prevExps = (prevSlot.experiments ?? {}) as Record<string, any>;
        const mergedExps: Record<string, any> = { ...prevExps };
        for (const [expId, liveExp] of Object.entries(liveExps)) {
          const prevExp = prevExps[expId];
          if (!prevExp) {
            mergedExps[expId] = liveExp;
            changed = true;
            continue;
          }
          const adopted: any = { ...prevExp };
          let expChanged = false;
          // Status progression: pending/draft → generating → completed/error.
          // The local state may be ahead of Firestore right after a Generate
          // click (we patch status='generating' optimistically before the
          // backend acknowledges). Refuse to overwrite a local optimistic
          // 'generating' with a stale Firestore 'pending' / 'draft' — only
          // accept forward transitions (completed / error / same value).
          const liveStatus = (liveExp as any).status;
          const prevStatus = prevExp.status;
          const isOptimisticAheadOfServer =
            prevStatus === 'generating' &&
            (liveStatus === 'pending' || liveStatus === 'draft' || liveStatus == null);
          for (const field of ['generatedUrl', 'status', 'generationModel', 'generatedAt', 'error'] as const) {
            if (field === 'status' && isOptimisticAheadOfServer) continue;
            if ((liveExp as any)[field] !== prevExp[field]) {
              adopted[field] = (liveExp as any)[field];
              expChanged = true;
            }
          }
          if (expChanged) {
            mergedExps[expId] = adopted;
            changed = true;
          }
        }
        if (changed) {
          next[slotKey] = { ...prevSlot, experiments: mergedExps };
        }
      }
      return changed ? next : prev;
    });
  }, [open, persistedId, live]);

  const togglePlatform = (p: PlatformId) => {
    if (readOnly) return;
    setPlatforms((prev) => {
      // Single-platform entities (ContentPost) keep a single selection;
      // multi-platform entities (PostExample) toggle freely.
      if (!isExample && post) {
        return prev.includes(p) ? [] : [p];
      }
      return prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p];
    });
  };

  const buildDraft = (nextAssets: Record<string, AiAssetSlot>): PostLike => ({
    ...(post ?? { ownerKind: 'author_post_example', ownerPath: '', projectId: '' } as any),
    id: persistedId,
    title: title.trim() || undefined,
    hook: hook.trim() || undefined,
    body: body.trim() || undefined,
    cta: cta.trim() || undefined,
    notes: notes.trim() || undefined,
    sourceUrl: sourceUrl.trim() || undefined,
    sourceCreatorHandle: sourceCreatorHandle.trim() || undefined,
    platforms,
    aiAssets: nextAssets,
  });

  const persistAssetsLocal = (nextAssets: Record<string, AiAssetSlot>) => {
    setAiAssets(nextAssets);
  };

  const flushDraft = async (assetsToPersist: Record<string, AiAssetSlot>): Promise<string> => {
    const id = await onSave(buildDraft(assetsToPersist));
    if (id && !persistedId) setPersistedId(id);
    return id || persistedId;
  };

  const canSubmit =
    !readOnly &&
    !submitting &&
    platforms.length > 0 &&
    Boolean(hook.trim() || body.trim() || title.trim());

  const handleSave = async () => {
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const id = await onSave(buildDraft(aiAssets));
      if (id && !persistedId) setPersistedId(id);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setError(null);
    try {
      await onDelete();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handlePromote = async () => {
    if (!onPromoteToExample) return;
    setError(null);
    setPromoting(true);
    try {
      await onPromoteToExample();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPromoting(false);
    }
  };

  const handleGenerateImage = async (config: GenerateImageConfig): Promise<GenerateImageResult> => {
    if (!onGenerateImage) {
      return { success: false, error: 'Image generation not available for this post kind' };
    }
    try {
      // Flush local draft before backend dispatch so the target doc has the
      // slot/experiment the backend will write results into.
      const id = await flushDraft(aiAssets);
      if (!id) {
        return { success: false, error: 'Unable to persist draft — no id returned' };
      }
      return await onGenerateImage(config);
    } catch (e) {
      return { success: false, error: (e as Error).message };
    }
  };

  // Audit block visible for post kinds only (never for PostExample).
  const showAudit =
    post &&
    post.ownerKind !== 'author_post_example' &&
    (post.whyForAudience?.length ||
      post.dataUsage ||
      post.status ||
      post.scheduledAt ||
      post.format ||
      post.failureReason ||
      post.dedupFingerprint);

  const canPromote = !!onPromoteToExample && post && post.ownerKind !== 'author_post_example';
  const facebookPublishMessage = [hook, body, cta]
    .map((part) => part.trim())
    .filter(Boolean)
    .join('\n\n');
  const showFacebookPublishPanel = Boolean(
    post?.projectId &&
    !isExample &&
    (
      publicationPlatform?.toLowerCase().includes('facebook') ||
      platforms.some((platform) => String(platform).toLowerCase().includes('facebook'))
    ),
  );
  const showXPublishPanel = Boolean(
    post?.projectId &&
    publicationId &&
    (publicationStrategyId || post?.strategyId) &&
    !isExample &&
    (
      publicationPlatform?.toLowerCase() === 'twitter' ||
      platforms.some((platform) => String(platform).toLowerCase() === 'twitter')
    ),
  );

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid={TESTIDS.dialog}
    >
      <DialogHeader
        title={
          isExample
            ? (isCreate ? 'Add post example' : 'Edit post example')
            : (isCreate ? 'New post' : 'Post details')
        }
        subtitle={
          isExample
            ? 'Curated reference content — fed to AI as a style anchor for linked topics'
            : readOnly
              ? 'Read-only generated task artifact. Open the editable location to make changes.'
              : 'Edit the generated post. Audit info is shown below.'
        }
        onClose={onClose}
      />
      <DialogContent sx={{ minHeight: 520, maxHeight: '75vh', overflow: 'auto' }}>
        {/* H.4 — Multi-platform tabs. Only shown when caller passes a map
            with > 1 entry (a topic with publications on multiple platforms). */}
        {showPlatformTabs && (
          <Tabs
            value={effectivePlatform ?? platformKeys[0]}
            onChange={(_, v) => {
              const p = v as PlatformId;
              setInternalSelectedPlatform(p);
              onSelectPlatform?.(p);
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mb: 1,
              position: 'sticky',
              top: 0,
              bgcolor: 'background.paper',
              zIndex: 1,
            }}
            data-testid="post-like-dialog-platform-tabs"
          >
            {platformKeys.map((p) => (
              <Tab
                key={p}
                label={p}
                value={p}
                data-testid={`post-like-dialog-platform-tab-${p}`}
              />
            ))}
          </Tabs>
        )}
        <Stack spacing={2} sx={{ py: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* H.4 — Refine button. Opens a prompt textarea modal that calls
              onRefine(userPrompt) — caller dispatches a regenerate task. */}
          {onRefine && !isCreate && !readOnly && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setRefineOpen(true)}
                data-testid="post-like-dialog-refine-btn"
              >
                Refine with prompt…
              </Button>
            </Box>
          )}

          <TextField
            label={isExample ? 'Title (optional library label)' : 'Title (optional)'}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            size="small"
            disabled={readOnly}
            inputProps={{ 'data-testid': TESTIDS.title }}
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>Target platforms</Typography>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              data-testid={TESTIDS.platforms}
            >
              {selectablePlatforms.map((p) => (
                <Chip
                  key={p}
                  label={p}
                  onClick={() => togglePlatform(p)}
                  color={platforms.includes(p) ? 'primary' : 'default'}
                  variant={platforms.includes(p) ? 'filled' : 'outlined'}
                  size="small"
                  disabled={readOnly}
                />
              ))}
            </Stack>
            {platforms.length === 0 && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                Pick at least one platform so the AI knows when to use this post.
              </Typography>
            )}
          </Box>

          <TextField
            label="Hook"
            value={hook}
            onChange={(e) => setHook(e.target.value)}
            size="small"
            placeholder="First line / opening sentence"
            disabled={readOnly}
            inputProps={{ 'data-testid': TESTIDS.hook }}
          />

          <TextField
            label="Body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            multiline minRows={4}
            disabled={readOnly}
            inputProps={{ 'data-testid': TESTIDS.body }}
          />

          <TextField
            label="CTA"
            value={cta}
            onChange={(e) => setCta(e.target.value)}
            size="small"
            disabled={readOnly}
            inputProps={{ 'data-testid': TESTIDS.cta }}
          />

          <TextField
            label={isExample ? 'Your notes (why this is a good example)' : 'Notes'}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline minRows={2}
            size="small"
            disabled={readOnly}
            inputProps={{ 'data-testid': TESTIDS.notes }}
            helperText={isExample ? 'Shown to the AI as additional commentary — explain what to emulate' : undefined}
          />

          <Stack direction="row" spacing={1}>
            <TextField
              label="Source URL (optional)"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              size="small"
              sx={{ flex: 2 }}
              disabled={readOnly}
              inputProps={{ 'data-testid': TESTIDS.sourceUrl }}
            />
            <TextField
              label="Creator handle"
              value={sourceCreatorHandle}
              onChange={(e) => setSourceCreatorHandle(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              placeholder="@handle"
              disabled={readOnly}
              inputProps={{ 'data-testid': TESTIDS.sourceHandle }}
            />
          </Stack>

          {!persistedId && (
            <Alert severity="info">
              Save first, then use AI asset slots to generate images.
            </Alert>
          )}

          {persistedId && (handleRegenerateBrief || handleRegeneratePost || designAssetBrief) && (
            <DesignBriefDisplay
              brief={designAssetBrief}
              isMaster={isMasterPost}
              busy={briefBusy}
              onRegenerateBrief={handleRegenerateBrief ?? (async () => undefined)}
              onRegeneratePost={handleRegeneratePost}
            />
          )}

          {persistedId && (
            <AiAssetsEditor
              projectId={post?.projectId ?? ''}
              aiAssets={aiAssets}
              onChange={persistAssetsLocal}
              onGenerateImage={onGenerateImage ? handleGenerateImage : undefined}
              onUploadReference={onUploadReference}
              onAssetPersist={async (next) => {
                if (onAssetPersist) await onAssetPersist(next);
                else await flushDraft(next);
              }}
              allowAddSlot
              disabled={readOnly}
              testIdPrefix="post-like-assets"
            />
          )}

          {showFacebookPublishPanel && post?.projectId && (
            <FacebookPagePublishPanel
              projectId={post.projectId}
              strategyId={post.strategyId}
              publicationId={publicationId}
              scheduledAt={publicationScheduledAt ?? post.scheduledAt}
              message={facebookPublishMessage}
              link={sourceUrl}
              initialStatus={publicationFacebookStatus}
              disabled={readOnly}
            />
          )}

          {showXPublishPanel && post?.projectId && publicationId && (publicationStrategyId || post.strategyId) && (
            <XManualPublicationPanel
              projectId={post.projectId}
              strategyId={(publicationStrategyId || post.strategyId)!}
              publicationId={publicationId}
              scheduledAt={publicationScheduledAt ?? post.scheduledAt}
              delivery={publicationDelivery}
              disabled={readOnly}
            />
          )}

          {showAudit && (
            <Accordion
              variant="outlined"
              disableGutters
              sx={{ mt: 1 }}
              data-testid={TESTIDS.audit}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Audit & generation metadata</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {post?.status && <Chip size="small" label={`status: ${post.status}`} />}
                    {post?.format && <Chip size="small" label={`format: ${post.format}`} variant="outlined" />}
                    {post?.scheduledAt && (
                      <Chip size="small" label={`scheduled: ${new Date(post.scheduledAt).toLocaleString()}`} variant="outlined" />
                    )}
                    {typeof post?.weekIndex === 'number' && (
                      <Chip size="small" label={`week ${post.weekIndex + 1}`} variant="outlined" />
                    )}
                    {post?.failureReason && (
                      <Chip size="small" color="error" label={`failure: ${post.failureReason}`} />
                    )}
                  </Stack>

                  {!!post?.whyForAudience?.length && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Why this matters for the audience
                      </Typography>
                      <Stack spacing={0.5}>
                        {post!.whyForAudience!.map((w: any, i: number) => (
                          <Typography key={i} variant="body2">
                            {typeof w === 'string' ? w : w?.reason ?? w?.summary ?? JSON.stringify(w)}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {post?.dataUsage && (post.dataUsage as any).items && (
                    <DataUsageList
                      items={(post.dataUsage as any).items}
                      maxItems={50}
                      testIdPrefix="post-like-dialog-data-usage"
                    />
                  )}

                  {post?.dedupFingerprint && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Dedup fingerprint
                      </Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        hook: {(post.dedupFingerprint as any).hookHash ?? '—'} · topic: {(post.dedupFingerprint as any).topicHash ?? '—'}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}
        </Stack>
      </DialogContent>
      <DialogFooter
        secondaryAction={
          onDelete && !isCreate && !readOnly
            ? ({ label: 'Delete', onClick: handleDelete, color: 'error', 'data-testid': TESTIDS.delete } as any)
            : ({ label: readOnly ? 'Close' : 'Cancel', onClick: onClose, 'data-testid': TESTIDS.cancel } as any)
        }
        additionalActions={
          readOnly
            ? readOnlyAdditionalActions
            : canPromote
            ? [
                {
                  label: promoting ? 'Saving…' : 'Save as example',
                  onClick: handlePromote,
                  variant: 'outlined',
                  disabled: promoting || submitting,
                  startIcon: <StarBorderIcon />,
                  'data-testid': TESTIDS.promote,
                } as any,
              ]
            : undefined
        }
        primaryAction={
          readOnly
            ? undefined
            : ({
                label: submitting ? 'Saving…' : (isCreate ? 'Add' : 'Save'),
                onClick: handleSave,
                disabled: !canSubmit,
                'data-testid': TESTIDS.save,
              } as any)
        }
      />
      {/* H.4 — Refine prompt modal */}
      {onRefine && (
        <Dialog
          open={refineOpen}
          onClose={() => !refining && setRefineOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <MuiDialogTitle>Refine with a prompt</MuiDialogTitle>
          <MuiDialogContent>
            <TextField
              autoFocus
              multiline
              minRows={3}
              fullWidth
              label="What should the AI do differently?"
              placeholder="e.g. make the hook more provocative; add a stat from the resources"
              value={refinePrompt}
              onChange={(e) => setRefinePrompt(e.target.value)}
              disabled={refining}
              inputProps={{ 'data-testid': 'post-like-dialog-refine-prompt' }}
              sx={{ mt: 1 }}
            />
          </MuiDialogContent>
          <DialogActions>
            <Button onClick={() => setRefineOpen(false)} disabled={refining}>
              Cancel
            </Button>
            <Button
              variant="contained"
              data-testid="post-like-dialog-refine-confirm"
              disabled={refining || !refinePrompt.trim()}
              onClick={async () => {
                setRefining(true);
                try {
                  await onRefine(refinePrompt.trim());
                  setRefineOpen(false);
                  setRefinePrompt('');
                } catch (e: any) {
                  setError(e?.message ?? 'Refine failed');
                } finally {
                  setRefining(false);
                }
              }}
            >
              {refining ? 'Refining…' : 'Refine'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </BaseDialog>
  );
};

export default UnifiedPostDialog;
